'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Heart, MessageCircle, Bookmark, Share2, Star, ExternalLink } from 'lucide-react';

interface Review {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  userBadge: boolean;
  cafeId: string;
  cafeName: string;
  cafeArea: string;
  rating: number;
  text: string;
  photos: string[];
  tags: string[];
  likes: number;
  comments: number;
  timeAgo: string;
  isLiked: boolean;
  isSaved: boolean;
}

interface ReviewPostCardProps {
  review: Review;
  onLike: () => void;
  onComment: () => void;
  onSave: () => void;
  onLikeConfirmed: () => void;
  onSaveConfirmed: () => void;
}

export default function ReviewPostCard({
  review,
  onLike,
  onComment,
  onSave,
  onLikeConfirmed,
  onSaveConfirmed,
}: ReviewPostCardProps) {
  const [shareOpen, setShareOpen] = useState(false);
  const IS_LOGGED_IN = false; // Backend: replace with actual auth state

  const handleLike = () => {
    if (!IS_LOGGED_IN) { onLike(); return; }
    onLikeConfirmed();
  };
  const handleSave = () => {
    if (!IS_LOGGED_IN) { onSave(); return; }
    onSaveConfirmed();
  };
  const handleComment = () => {
    if (!IS_LOGGED_IN) { onComment(); return; }
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(`Cek ${review.cafeName} di Nongki! "${review.text.slice(0, 80)}..."`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShareOpen(false);
  };

  return (
    <div className="review-card animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-border">
            <AppImage
              src={review.userAvatar}
              alt={`Foto profil ${review.userName}`}
              fill
              className="object-cover"
              sizes="40px"
            />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-700 text-foreground">{review.userName}</span>
              {review.userBadge && <span className="nongki-badge">★ Nongki</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-muted-foreground">{review.timeAgo}</span>
              <span className="text-muted-foreground text-xs">·</span>
              <Link href={`/coffeshop-detail-page?id=${review.cafeId}`} className="text-xs text-accent font-600 hover:underline">
                📍 {review.cafeName}
              </Link>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-secondary rounded-lg px-2 py-1">
          <Star size={12} className="rating-star fill-current" />
          <span className="text-xs font-700 text-primary font-tabular">{review.rating.toFixed(1)}</span>
        </div>
      </div>

      {/* Text */}
      <div className="px-4 pb-3">
        <p className="text-sm text-foreground leading-relaxed line-clamp-3">{review.text}</p>
      </div>

      {/* Photos */}
      {(review.photos?.length ?? 0) > 0 && (
        <div className={`px-4 pb-3 grid gap-2 ${review.photos.length === 1 ? 'grid-cols-1' : review.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {review.photos.map((photo, i) => (
            <div key={`photo-${review.id}-${i}`} className="relative rounded-xl overflow-hidden aspect-square">
              <AppImage
                src={photo}
                alt={`Foto review ${review.cafeName} oleh ${review.userName}, foto ${i + 1}`}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 33vw, 200px"
              />
            </div>
          ))}
        </div>
      )}

      {/* Tags */}
      {(review.tags?.length ?? 0) > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {review.tags.map((tag) => (
            <span key={`rtag-${review.id}-${tag}`} className="tag-pill">{tag}</span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="px-4 pb-4 pt-2 border-t border-border flex items-center gap-4">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 text-sm font-500 transition-colors btn-press ${review.isLiked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`}
        >
          <Heart size={17} className={review.isLiked ? 'fill-current' : ''} />
          <span className="font-tabular">{review.likes}</span>
        </button>
        <button
          onClick={handleComment}
          className="flex items-center gap-1.5 text-sm font-500 text-muted-foreground hover:text-primary transition-colors btn-press"
        >
          <MessageCircle size={17} />
          <span className="font-tabular">{review.comments}</span>
        </button>
        <button
          onClick={handleSave}
          className={`flex items-center gap-1.5 text-sm font-500 transition-colors btn-press ${review.isSaved ? 'text-accent' : 'text-muted-foreground hover:text-accent'}`}
        >
          <Bookmark size={17} className={review.isSaved ? 'fill-current' : ''} />
        </button>

        {/* Share */}
        <div className="relative ml-auto">
          <button
            onClick={() => setShareOpen(!shareOpen)}
            className="flex items-center gap-1.5 text-sm font-500 text-muted-foreground hover:text-primary transition-colors btn-press"
          >
            <Share2 size={17} />
          </button>
          {shareOpen && (
            <div className="absolute bottom-full right-0 mb-2 bg-card border border-border rounded-xl shadow-card overflow-hidden z-10 w-44 animate-scale-in">
              <button
                onClick={shareToWhatsApp}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-secondary transition-colors"
              >
                <span className="text-base">💬</span> WhatsApp
              </button>
              <button
                onClick={() => setShareOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-secondary transition-colors border-t border-border"
              >
                <span className="text-pink-500 text-base">📸</span> Instagram Story
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(window.location.href); setShareOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-secondary transition-colors border-t border-border"
              >
                <ExternalLink size={16} className="text-primary" /> Salin Link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}